import { Injectable } from '@angular/core';

import { ignoreElementsTyped } from '@frontend/sports/common/core/utils/rxjs';
import { TrackingService, trackingConstants } from '@frontend/sports/tracking/feature';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { NumpadAction } from 'packages/sports/web/app/src/numpad/model';
import { map, tap, withLatestFrom } from 'rxjs';

import { IBetslipRootState } from '../../../base/store/state';
import { PlaceButtonTracking, PlaceButtonTrackingService } from '../../betplacement/services/place-button/place-button-tracking.service';
import BetslipPickActions from '../../picks/actions';
import BetslipErrorsActions from '../../validation/actions';
import { QB_TRACKING_SUFFIX } from '../const';
import { QuickBetActions } from '../quick-bet-actions';
import { quickBetStateSelector, selectQuickBetPickIdsOrBetbuilderId, selectTrackingDetails } from '../quick-bet.selectors';
import { QuickBetActionButtonState } from '../quick-bet.state';
import { QuickBetTrackingService } from '../services/quick-bet-tracking.service';

@Injectable()
export class QuickBetTrackingEffects {
    constructor(
        private actions$: Actions,
        private trackingService: TrackingService,
        private quickBetTrackingService: QuickBetTrackingService,
        private store: Store<IBetslipRootState>,
        private placeButtonTrackingService: PlaceButtonTrackingService,
    ) {}

    handleOpenQuickBetEffect$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(QuickBetActions.openQuickBet),
                tap(() => {
                    this.quickBetTrackingService.trackLoad();
                }),
                ignoreElementsTyped(),
            );
        },
        { dispatch: false },
    );

    handleActionButtonClickEffect$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(QuickBetActions.actionButtonClick),
                withLatestFrom(this.store.select(selectTrackingDetails)),
                tap(([action, trackingDetails]) => {
                    switch (action.payload.state) {
                        case QuickBetActionButtonState.Login:
                            this.placeButtonTrackingService.track(PlaceButtonTracking.LoginToBet, PlaceButtonTracking.QuickBet);
                            break;
                        case QuickBetActionButtonState.Deposit:
                            this.placeButtonTrackingService.track(PlaceButtonTracking.Deposit, PlaceButtonTracking.QuickBet);
                            break;
                        case QuickBetActionButtonState.MakeDeposit:
                            this.placeButtonTrackingService.track(PlaceButtonTracking.MakeDeposit, PlaceButtonTracking.QuickBet);
                            break;
                        case QuickBetActionButtonState.DepositAndPlaceBet:
                            this.placeButtonTrackingService.track(PlaceButtonTracking.DepositAndPlace, PlaceButtonTracking.QuickBet);
                            break;
                        case QuickBetActionButtonState.AcceptChanges:
                            this.placeButtonTrackingService.track(PlaceButtonTracking.AcceptChanges, PlaceButtonTracking.QuickBet);
                            break;
                        case QuickBetActionButtonState.AcceptAndPlace:
                            this.placeButtonTrackingService.track(PlaceButtonTracking.AcceptAndPlace, PlaceButtonTracking.QuickBet);
                            break;

                        case QuickBetActionButtonState.AcceptAndPlaceFreeBet:
                            this.placeButtonTrackingService.track(PlaceButtonTracking.AcceptAndPlaceFreeBet, PlaceButtonTracking.QuickBet);
                            break;
                        case QuickBetActionButtonState.Place:
                            this.placeButtonTrackingService.trackQuickBetPlaceBet(
                                PlaceButtonTracking.PlaceBet,
                                PlaceButtonTracking.QuickBet,
                                trackingDetails,
                            );
                            break;
                        case QuickBetActionButtonState.PlaceFreeBet:
                            this.placeButtonTrackingService.trackQuickBetPlaceBet(
                                PlaceButtonTracking.PlaceFreeBet,
                                PlaceButtonTracking.QuickBet,
                                trackingDetails,
                            );
                            break;
                        case QuickBetActionButtonState.UpdateMinStake:
                            this.placeButtonTrackingService.updateStakeTrackData(PlaceButtonTracking.QuickBet, PlaceButtonTracking.UpdateStake);
                            break;
                    }
                }),
                ignoreElementsTyped(),
            );
        },
        { dispatch: false },
    );

    handleAddToBetslipButtonClickEffect$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(QuickBetActions.openBetslip),
                withLatestFrom(this.store.select(selectTrackingDetails)),
                tap(([_, trackingDetails]) => {
                    this.quickBetTrackingService.trackViewInBetslip(trackingDetails);
                }),
                ignoreElementsTyped(),
            );
        },
        { dispatch: false },
    );

    handleRemovePickButtonClickEffect$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(QuickBetActions.removePickButtonClick),
                tap(() => {
                    this.trackingService.triggerEventWithCleaning(trackingConstants.EVENT_INIT_QUICKBET, {
                        [trackingConstants.PAGE_REFERRING_ACTION]: 'QuickBet_RemovePick_Button_Clicked',
                    });
                }),
                ignoreElementsTyped(),
            );
        },
        { dispatch: false },
    );

    handleHelpCloseEffect$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(QuickBetActions.hideHelp),
                tap(() => {
                    this.trackingService.triggerEventWithCleaning(trackingConstants.EVENT_INIT_QUICKBET, {
                        [trackingConstants.PAGE_REFERRING_ACTION]: 'QuickBet_Onboarding_Close',
                    });
                }),
                ignoreElementsTyped(),
            );
        },
        { dispatch: false },
    );

    handleAddPickTrackingSuffix$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(QuickBetActions.addPick),
            map((action) =>
                BetslipPickActions.setSourceOfBetSuffix({
                    pickId: action.pickId,
                    suffix: QB_TRACKING_SUFFIX,
                }),
            ),
        );
    });

    handleQBPickNumpadActionTracking$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(QuickBetActions.numpadAction),
            concatLatestFrom(() => [this.store.select(selectQuickBetPickIdsOrBetbuilderId)]),
            map(([action, pickIds]) => BetslipPickActions.setTrackingNumpadAction({ pickIds, action: action.action })),
        );
    });

    handleCloseQBTrackingSuffixRemoval$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(QuickBetActions.closeQuickbetUi),
            concatLatestFrom(() => this.store.select(quickBetStateSelector)),
            map(([_, state]) => BetslipPickActions.setMultipleSourceOfBetSuffix({ pickIds: state.pickIds, suffix: '' })),
        );
    });

    handleCloseQBPickNumpadActionTrackinRemoval$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(QuickBetActions.closeQuickbetUi),
            concatLatestFrom(() => [this.store.select(selectQuickBetPickIdsOrBetbuilderId)]),
            map(([_, pickIds]) => BetslipPickActions.setTrackingNumpadAction({ pickIds, action: NumpadAction.None })),
        );
    });

    handleQuickBetClientErrorsTrackingEffect$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(BetslipErrorsActions.trackQuickBetClientErrors),
                withLatestFrom(this.store.select(selectTrackingDetails)),
                tap(([action, trackingDetails]) => {
                    this.quickBetTrackingService.trackQuickBetClientErrors(action.trackingData, trackingDetails);
                }),

                ignoreElementsTyped(),
            );
        },
        { dispatch: false },
    );

    handleQuickBetCloseButtonClickTrackingEffect$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(QuickBetActions.closeQuickbetUi),
                withLatestFrom(this.store.select(selectTrackingDetails)),
                tap(([, trackingDetails]) => {
                    this.quickBetTrackingService.trackCloseButtonClick(trackingDetails);
                }),

                ignoreElementsTyped(),
            );
        },
        { dispatch: false },
    );

    handleRemovePicksFromQuickBetTrackingEffect$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(QuickBetActions.removePickButtonClick),
                tap((action) => {
                    this.quickBetTrackingService.trackRemovePicksButtonClick(action.trackingDetails);
                }),

                ignoreElementsTyped(),
            );
        },
        { dispatch: false },
    );
}
